import * as Utils from "../Utils";
import { adminFetch, inspectAdminResponse, isLegacyNotAuthorizedMessage } from "./admin_api";

const getAuthToken = () => localStorage.getItem("adminToken");

/**
 * GET /api/v1/payment/admin/history
 */
export async function fetchPaymentHistory(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, value);
    }
  });

  const queryPart = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(
    `${Utils.API_URL}v1/payment/admin/history${queryPart}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
    }
  );

  const data = await response.json();

  if (inspectAdminResponse(response, data)) {
    const forbidden =
      response.status === 403 || isLegacyNotAuthorizedMessage(data?.message);
    const error = new Error(
      forbidden ? "Not authorized" : "Session expired. Please sign in again."
    );
    error.status = forbidden ? 403 : 401;
    error.response = data;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(data?.message || "Failed to fetch payment history");
    error.response = data;
    throw error;
  }

  return data;
}

/**
 * POST /api/v1/payment/admin/cashfree/verify
 * Admin Bearer token. Body accepts { orderId } OR { cashfreeOrderId }.
 * @param {{ orderId?: string, cashfreeOrderId?: string }} payload
 */
export async function verifyCashfreePaymentAdmin(payload) {
  const orderId = payload?.orderId;
  const cashfreeOrderId = payload?.cashfreeOrderId;
  if (!orderId && !cashfreeOrderId) {
    const error = new Error("orderId or cashfreeOrderId is required.");
    error.status = 400;
    throw error;
  }

  const body = orderId ? { orderId } : { cashfreeOrderId };

  try {
    const data = await adminFetch(`${Utils.API_URL}v1/payment/admin/cashfree/verify`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify(body),
    });
    return data;
  } catch (error) {
    if (!error.response && error.status) {
      throw error;
    }
    throw error;
  }
}

/**
 * Map Cashfree verify API result to a user-facing toast message.
 */
export function getCashfreeVerifyMessage(result, error) {
  if (error) {
    if (error.status === 401) {
      return { type: "error", text: "Session expired. Please sign in again." };
    }
    if (error.status === 503) {
      return {
        type: "error",
        text: error.message || "Cashfree is not configured on the server.",
      };
    }
    return {
      type: "error",
      text: error.message || "Could not verify Cashfree payment.",
    };
  }

  if (result?.success) {
    const gatewayStatus = String(result?.paymentStatus || "").toUpperCase();
    const alreadyDone =
      result?.alreadyVerified ||
      gatewayStatus === "SUCCESS" ||
      gatewayStatus === "COMPLETED" ||
      result?.message?.toLowerCase?.().includes("already");

    if (alreadyDone) {
      return {
        type: "success",
        text: result.message || "Payment verified — status updated to Completed.",
      };
    }
  }

  const gatewayStatus = String(result?.paymentStatus || "").toUpperCase();
  if (gatewayStatus === "PENDING" || gatewayStatus === "USER_DROPPED") {
    return {
      type: "warning",
      text:
        result?.message ||
        "Cashfree reports payment still pending. Ask the customer to retry or check the gateway dashboard.",
    };
  }

  if (gatewayStatus === "MISMATCH") {
    return {
      type: "error",
      text:
        result?.reason === "amount_mismatch"
          ? "Amount mismatch: payment received, refund/review needed"
          : result?.message || "Payment amount mismatch detected — manual review required",
    };
  }

  if (gatewayStatus === "FAILED") {
    return {
      type: "error",
      text: result?.message || "Cashfree reports payment failed.",
    };
  }

  return {
    type: "warning",
    text: result?.message || "Payment not completed at Cashfree.",
  };
}
